import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Crown,
  Zap,
  Star,
  Download,
  Calendar,
  BarChart3,
  Settings
} from 'lucide-react'

interface Template {
  id: string
  name: string
  category: string
  price: number
  sales: number
  revenue: number
  rating: number
  downloads: number
  createdAt: string
  isActive: boolean
  isPremium: boolean
}

interface Earnings {
  totalRevenue: number
  monthlyRevenue: number
  totalSales: number
  averagePrice: number
  topPerforming: string
  growthRate: number
}

export default function CreatorDashboard() {
  const { address, isConnected } = useWallet()
  const [templates, setTemplates] = useState<Template[]>([])
  const [earnings, setEarnings] = useState<Earnings | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d')
  const [activeTab, setActiveTab] = useState<'overview' | 'templates' | 'analytics' | 'settings'>('overview')

  // Mock data - in production this would come from blockchain
  useEffect(() => {
    if (isConnected) {
      const mockTemplates: Template[] = [
        {
          id: '1',
          name: 'Music Creator Pro',
          category: 'music',
          price: 50,
          sales: 125,
          revenue: 6250,
          rating: 4.9,
          downloads: 1250,
          createdAt: '2024-01-15',
          isActive: true,
          isPremium: true
        },
        {
          id: '2',
          name: 'Visual Artist Studio',
          category: 'artist',
          price: 35,
          sales: 89,
          revenue: 3115,
          rating: 4.8,
          downloads: 890,
          createdAt: '2024-01-20',
          isActive: true,
          isPremium: false
        },
        {
          id: '3',
          name: 'Creator Hub',
          category: 'portfolio',
          price: 75,
          sales: 210,
          revenue: 15750,
          rating: 4.9,
          downloads: 2100,
          createdAt: '2024-01-10',
          isActive: true,
          isPremium: true
        }
      ]

      const mockEarnings: Earnings = {
        totalRevenue: 25115,
        monthlyRevenue: 8540,
        totalSales: 424,
        averagePrice: 59.2,
        topPerforming: 'Creator Hub',
        growthRate: 23.5
      }

      setTemplates(mockTemplates)
      setEarnings(mockEarnings)
    }
  }, [isConnected])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        <div className="text-center">
          <Crown className="w-16 h-16 text-gold-400 mx-auto mb-6" />
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Creator Dashboard
          </h2>
          <p className="text-purple-200 mb-8 text-lg">
            Please connect your wallet to access your creator dashboard
          </p>
          <Link
            to="/"
            className="bg-gold-500 text-black px-8 py-4 rounded-lg hover:bg-gold-600 transition-colors font-semibold"
          >
            Go Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-purple-900 text-white">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
              radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
            `,
            backgroundSize: '100% 100%, 800px 800px, 900px 900px'
          }}
        />
        
        <div className="relative z-10 py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 
                  className="text-4xl md:text-6xl font-bold mb-2"
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                    background: 'linear-gradient(45deg, #F59E0B, #8B5CF6, #EF4444)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Creator Dashboard
                </h1>
                <p className="text-xl text-purple-200">
                  Manage your templates and track your earnings
                </p>
              </div>
              
              <Link
                to="/create-template"
                className="bg-gold-500 text-black px-6 py-3 rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2 font-semibold"
              >
                <Plus className="w-5 h-5" />
                Create Template
              </Link>
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-2 mb-8">
              {[
                { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
                { id: 'templates', label: 'Templates', icon: <Zap className="w-4 h-4" /> },
                { id: 'analytics', label: 'Analytics', icon: <TrendingUp className="w-4 h-4" /> },
                { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-gold-500 text-black'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && earnings && (
            <div className="space-y-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Total Revenue</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(earnings.totalRevenue)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(earnings.monthlyRevenue)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Total Sales</p>
                      <p className="text-2xl font-bold text-white">{formatNumber(earnings.totalSales)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gold-500/20 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-gold-400" />
                    </div>
                    <div>
                      <p className="text-purple-200 text-sm">Growth Rate</p>
                      <p className="text-2xl font-bold text-white">+{earnings.growthRate}%</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Recent Activity</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Template sale: Music Creator Pro</p>
                        <p className="text-purple-200 text-sm">2 hours ago</p>
                      </div>
                    </div>
                    <span className="text-green-400 font-semibold">+$50</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Download className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">New download: Visual Artist Studio</p>
                        <p className="text-purple-200 text-sm">5 hours ago</p>
                      </div>
                    </div>
                    <span className="text-blue-400 font-semibold">+1 download</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Star className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">New review: Creator Hub (5 stars)</p>
                        <p className="text-purple-200 text-sm">1 day ago</p>
                      </div>
                    </div>
                    <span className="text-purple-400 font-semibold">5⭐</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">Your Templates</h3>
                <Link
                  to="/create-template"
                  className="bg-gold-500 text-black px-4 py-2 rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create New
                </Link>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden hover:bg-white/15 transition-all duration-300"
                  >
                    <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center relative">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Zap className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-white text-sm">{template.name}</p>
                      </div>
                      
                      {template.isPremium && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-gold-500 text-black px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Premium
                          </span>
                        </div>
                      )}
                      
                      <div className="absolute top-3 right-3">
                        <span className="bg-black/50 text-white px-2 py-1 rounded text-xs font-semibold">
                          {template.price} SUI
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-xl font-semibold text-white">{template.name}</h4>
                        <div className="flex items-center gap-1 text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm">{template.rating}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <p className="text-purple-200">Sales</p>
                          <p className="text-white font-semibold">{formatNumber(template.sales)}</p>
                        </div>
                        <div>
                          <p className="text-purple-200">Revenue</p>
                          <p className="text-white font-semibold">{formatCurrency(template.revenue)}</p>
                        </div>
                        <div>
                          <p className="text-purple-200">Downloads</p>
                          <p className="text-white font-semibold">{formatNumber(template.downloads)}</p>
                        </div>
                        <div>
                          <p className="text-purple-200">Category</p>
                          <p className="text-white font-semibold capitalize">{template.category}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button className="flex-1 bg-purple-500/20 text-purple-300 py-2 px-3 rounded-lg hover:bg-purple-500/30 transition-colors text-sm">
                          <Eye className="w-4 h-4 inline mr-1" />
                          View
                        </button>
                        <button className="bg-white/10 text-white py-2 px-3 rounded-lg hover:bg-white/20 transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="bg-red-500/20 text-red-300 py-2 px-3 rounded-lg hover:bg-red-500/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Analytics Coming Soon</h3>
              <p className="text-purple-200">
                Detailed analytics and insights will be available soon, including:
              </p>
              <ul className="text-purple-200 mt-4 space-y-2">
                <li>• Sales trends and patterns</li>
                <li>• User engagement metrics</li>
                <li>• Geographic distribution of buyers</li>
                <li>• Template performance comparisons</li>
                <li>• Revenue forecasting</li>
              </ul>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Settings</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-white mb-2">Revenue Share Percentage</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      defaultValue="75"
                      className="flex-1"
                    />
                    <span className="text-gold-400 font-semibold">75%</span>
                  </div>
                  <p className="text-purple-200 text-sm mt-2">
                    You receive 75% of each template sale, 25% goes to platform fees
                  </p>
                </div>
                
                <div>
                  <label className="block text-white mb-2">Payout Address</label>
                  <input
                    type="text"
                    value={address || ''}
                    readOnly
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-white mb-2">Email Notifications</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4 text-gold-600" />
                      <span className="text-purple-200">New sales notifications</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4 text-gold-600" />
                      <span className="text-purple-200">Weekly earnings summary</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="w-4 h-4 text-gold-600" />
                      <span className="text-purple-200">Marketing updates</span>
                    </label>
                  </div>
                </div>
                
                <button className="bg-gold-500 text-black px-6 py-3 rounded-lg hover:bg-gold-600 transition-colors">
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
